import Bugsnag from '@bugsnag/js';

import { proxy } from 'valtio';
import { setToken } from './api/common';
import { getOrganization } from './api/organizations2';
import { getMe as apiGetMe } from './api/user2';
import authorizations from './authorizations';
import authStore from './authStore';
import { capturePosthogEvent, POSTHOG_EVENTS } from './common/posthogEvents';
import { getPref, Prefs, setPref } from './common/prefs';
import type { MapStyle, OrganizationDetail, Project, TLoadedState, TLoadingState, User } from './common/types';
import { bootstrapCustomBasemaps } from './components2/Map/Widgets/BasemapPicker';
import getConfig from './config';
import { PYLON_CLIENT_APP_ID } from './pylon-widget';
import { applyLayerConfigWithFallback } from './store2/applyDbLayerConfig';
import layerStore from './store2/layers';

const config = getConfig();

export const DISCLAIMER_VIEWED_KEY = 'cg.disclaimerViewed';

import { updateBookmarks } from './api/user2';
import {
  capturePosthogInitialPageview,
  enablePosthogCapturing,
  setPosthogOrganization,
  setPosthogPersonProperties,
  setPosthogUser,
} from './common/posthog';

export const LoadingState: TLoadingState = { isLoading: true };

class Store {
  me: User | null = null;
  myRole?: { id: string; name: string; roleId: string };
  currentOrganizationId: string = '';
  currentOrganizationDetail: TLoadingState | (TLoadedState & OrganizationDetail) = LoadingState;
  projects: TLoadingState | (TLoadedState & Project[]) = LoadingState;
  preferredBasemap?: MapStyle;
  inUnverifiedMode = false;
  disclaimerViewed = config.env === 'dev' || localStorage.getItem(DISCLAIMER_VIEWED_KEY) === 'true';
  // inOnboardingMode = !window.localStorage.getItem('cg.onboardingComplete');
  inOnboardingMode = false;
  isUserBootstrapped = false;
  projectFilters: {
    constructionYear: number[];
    billingTier: string[];
    createdAtYear: number[];
  } = {
    constructionYear: [],
    billingTier: [],
    createdAtYear: [],
    ...(getPref(Prefs.PROJECT_LIST_FILTER) ? JSON.parse(getPref(Prefs.PROJECT_LIST_FILTER)!) : {}),
  };

  get currentOrganization(): OrganizationDetail | undefined {
    if (this.currentOrganizationDetail.isLoading === false) {
      return this.currentOrganizationDetail;
    } else {
      return undefined;
    }
  }

  async bootstrap(token: string) {
    // Once the org request is running, its own handlers send the initial pageview.
    let organizationLoadStarted = false;
    // reset everything pre start
    this.me = null;
    this.projects = { isLoading: true };
    this.currentOrganizationId = '';
    this.myRole = undefined;
    this.currentOrganizationDetail = { isLoading: true };
    this.inUnverifiedMode = false;

    try {
      // if `getMe` works, then the token is good
      const me = await apiGetMe(token);
      this.me = me;

      setToken(token);

      Bugsnag.setUser(me.id, me.email);
      window.pylon = {
        chat_settings: {
          app_id: PYLON_CLIENT_APP_ID,
          email: me.email,
          name: me.name,
          email_hash: authStore.pylonEmailHash,
        },
      };
      // Opt in first: `$identify` is dropped while capture is opted out.
      enablePosthogCapturing();
      setPosthogUser({ id: me.id, email: me.email });

      // Last used org when it still exists, else the first one.
      const lastUsedOrgId = getPref(Prefs.LAST_USED_ORG_ID);
      const orgId =
        lastUsedOrgId && me.organizations.some((o: { id: string }) => o.id === lastUsedOrgId)
          ? lastUsedOrgId
          : me.organizations[0].id;
      organizationLoadStarted = true;
      this.setCurrentOrganization(orgId).catch((e) => {
        console.error(e, 'Organization load failed');
        // No org group will be registered; count the page load without one.
        capturePosthogInitialPageview();
      });

      bootstrapCustomBasemaps();

      // configure map layers.
      layerStore.fetchTilesConfig();

      // set preferred basemap for home map and project details map
      const basemapString = getPref(Prefs.LAST_USED_BASEMAP);
      if (basemapString) {
        this.preferredBasemap = JSON.parse(basemapString);
      }
    } catch (e) {
      console.error(e, 'Bootstrap failed');
      // Sent only if capture was already opted in (the user was known); no-op otherwise.
      if (!organizationLoadStarted) capturePosthogInitialPageview();
    }
  }

  async setCurrentOrganization(organizationId: string) {
    this.currentOrganizationDetail.isLoading = true;
    // retrieve full org details from the backend

    const detail = await getOrganization(organizationId);

    // Capture `layerConfig` as PLAIN data BEFORE the store assignment below. `applyDbDrivenLayerConfig`
    // deep-clones `layerConfig.toc` via `structuredClone`, which throws `DataCloneError` on a valtio
    // Proxy. valtio (v2) proxifies nested refs IN PLACE, and `{ ...detail }` is a shallow spread, so
    // `detail.layerConfig` would otherwise become a Proxy too (it shares the nested ref valtio wraps).
    // `detail` is still fully plain at this point, so clone here — CG-366.
    const plainLayerConfig = detail.layerConfig ? structuredClone(detail.layerConfig) : undefined;

    this.currentOrganizationDetail = {
      ...detail,
      isLoading: false,
    };

    const { specialDatasets } = detail;
    // Swap the active TOC tree + digitization bundle to the DB-derived config before
    // setTOCLayersConfig clones it; falls back to legacy when the server delivered nothing.
    //
    // Guarded (CG-366): a layer-config failure must NOT block the core bootstrap state set
    // below (`currentOrganizationId` / `isUserBootstrapped`). Unguarded, a throw here aborts
    // the org switch entirely — the projects list silently empties and /admin hangs on a
    // spinner. On failure we force every bundle back to legacy and continue; layer config is
    // best-effort, core nav is not.
    applyLayerConfigWithFallback(
      plainLayerConfig,
      () => layerStore.setTOCLayersConfig({ specialDatasets }),
      () => layerStore.clearVisibleLinkedOrganizationIds(),
    );
    // customLayersTilesConfig is org-scoped; drop the memoized fetch so it refetches.
    layerStore.resetTilesConfigCache();

    this.projects.isLoading = true;
    this.currentOrganizationId = organizationId;

    setPosthogOrganization({
      id: organizationId,
      name: this.currentOrganizationDetail.name,
    });
    // The org group is registered, so the page load's `$pageview` now carries it.
    capturePosthogInitialPageview();

    // persist orgId for load later
    setPref(Prefs.LAST_USED_ORG_ID, organizationId);

    // reset role details
    this.myRole = undefined;
    authorizations.reset();
    // setup my actions for this org
    const myRole = this.currentOrganizationDetail.members.find((m) => m.user.id === this.me!.id);
    if (myRole) {
      const myOrganizationRole = this.currentOrganizationDetail.roles.find((r) => r.roleId === myRole.role);
      if (myOrganizationRole) {
        this.myRole = myOrganizationRole;
        authorizations.setup(myOrganizationRole.actions);

        this.isUserBootstrapped = true;

        setPosthogPersonProperties({ role: myOrganizationRole.name });
      } else {
        // this should never happen
        console.warn(`${myRole.role} is not a role in ${this.currentOrganizationDetail.id}`);
      }
    } else {
      // this should never happen
      console.warn(`${this.me!.id} is not a member of ${this.currentOrganizationDetail.id}`);
    }
  }

  async refreshMe() {
    // Cookie sessions have no token; an empty one makes getMe authenticate via the cookie.
    this.me = await apiGetMe(authStore.token ?? '');
  }

  setProjectFilters(value: typeof this.projectFilters) {
    this.projectFilters = Object.assign({}, value);
    setPref(Prefs.PROJECT_LIST_FILTER, JSON.stringify(this.projectFilters));
  }

  get filteredProjects(): TLoadingState | (TLoadedState & Project[]) {
    if (this.projects.isLoading) {
      return LoadingState;
    } else {
      let filtered = [...this.projects];
      Object.keys(this.projectFilters).forEach((key) => {
        if (this.projectFilters[key as keyof typeof this.projectFilters].length > 0) {
          filtered = filtered.filter((p) =>
            this.projectFilters[key as keyof typeof this.projectFilters].includes(p[key as keyof Project] as never),
          );
        }
      });

      return filtered as TLoadedState & Project[];
    }
  }

  setinUnverifiedMode(value: boolean) {
    this.inUnverifiedMode = value;
  }

  setInOnboardingMode(inOnboardingMode: boolean) {
    !inOnboardingMode && window.localStorage.setItem('cg.onboardingComplete', 'complete');
    this.inOnboardingMode = inOnboardingMode;
  }

  async toggleBookmark(projectId: string) {
    if (!this.me) {
      console.error('projects not loaded');
      return;
    }

    const originalBookmarks = [...(this.me.preferences.projectBookmarks || [])];

    const projects = this.projects as TLoadedState & Project[];

    const projectIndex = projects.findIndex((p) => p.id === projectId);

    if (projectIndex === -1) {
      console.error('Project not found');
      return;
    }

    const bookmarksSet = new Set(this.me.preferences.projectBookmarks || []);

    const updatedProject = { ...projects[projectIndex] };

    let trackingValue: 'added' | 'removed';

    if (bookmarksSet.has(projectId)) {
      bookmarksSet.delete(projectId);
      updatedProject.bookmarked = false;
      trackingValue = 'removed';
    } else {
      bookmarksSet.add(projectId);
      updatedProject.bookmarked = true;
      trackingValue = 'added';
    }

    capturePosthogEvent(POSTHOG_EVENTS.projectBookmarked, {
      project_id: updatedProject.id,
      organization_id: updatedProject.organization,
      action: trackingValue,
    });

    this.projects.isLoading = true;
    // Update the projects array
    const updatedProjects = [...projects.slice(0, projectIndex), updatedProject, ...projects.slice(projectIndex + 1)];

    // update this.projects while maintaining its type
    this.projects = updatedProjects as TLoadedState & Project[];

    this.projects.isLoading = false;

    // update the user's bookmarks in the valtio store
    this.me = {
      ...this.me,
      preferences: {
        ...this.me.preferences,
        projectBookmarks: Array.from(bookmarksSet),
      },
    };

    // try to update the bookmarks on the server, if not revert the optimistic update
    try {
      await updateBookmarks(projectId);
    } catch (error) {
      console.error('Failed to update bookmarks on the server', error);
      // Revert the optimistic update
      this.me = {
        ...this.me,
        preferences: {
          ...this.me.preferences,
          projectBookmarks: originalBookmarks,
        },
      };

      this.projects = projects;
    }
  }
}

const store = proxy(new Store());

export default store;
